import React from "react";
import { motion } from "framer-motion";
import { Send } from "lucide-react";
import { AuthForm } from "./AuthForm";

function LiveBubble({ msg }) {
  const isUser = msg.sender === "visitor";
  const name = msg.author_label || (isUser ? "You" : "Support");
  return (
    <motion.div
      initial={{ opacity: 0, x: isUser ? 12 : -12, scale: 0.98 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      className={`flex flex-col gap-1 ${isUser ? "items-end" : "items-start"}`}>
      <span className="text-[8px] sm:text-[9px] font-black uppercase tracking-widest text-slate-500 px-1">
        {name}
      </span>
      <div
        className={`max-w-[80%] sm:max-w-[85%] px-3 md:px-5 py-2 md:py-3.5 text-[11px] md:text-[13px] leading-relaxed shadow-xl break-words whitespace-pre-wrap font-medium tracking-tight ${
          isUser ?
            "bg-[#00BCF0] text-[#0B0F19] rounded-xl md:rounded-2xl rounded-tr-none italic font-black shadow-[0_5px_15px_rgba(0,188,240,0.2)]"
          : "bg-white/5 backdrop-blur-md text-slate-200 border border-white/10 rounded-xl md:rounded-2xl rounded-tl-none"
        }`}>
        {msg.content}
      </div>
    </motion.div>
  );
}

export function LiveChatTab({
  t,
  showOffline,
  canUseLive,
  authMode,
  setAuthMode,
  formData,
  setFormData,
  authError,
  authBusy,
  showPassword,
  setShowPassword,
  onAuthSubmit,
  onGoogleSignIn,
  initError,
  liveReady,
  messages,
  input,
  setInput,
  onSendMessage,
  sending,
}) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex-grow min-h-0 overflow-y-auto space-y-3 md:space-y-5 custom-scrollbar p-3 md:p-5">
        {showOffline && (
          <p className="text-xs md:text-sm text-slate-400 leading-relaxed border border-white/10 rounded-2xl p-3 md:p-4 bg-white/5">
            {t.offlineHint}
          </p>
        )}
        {!showOffline && !canUseLive && (
          <AuthForm
            t={t}
            authMode={authMode}
            setAuthMode={setAuthMode}
            formData={formData}
            setFormData={setFormData}
            authError={authError}
            authBusy={authBusy}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            onSubmit={onAuthSubmit}
            onGoogleSignIn={onGoogleSignIn}
          />
        )}

        {canUseLive && initError && (
          <p className="text-xs text-red-300/90 border border-red-500/20 rounded-lg md:rounded-xl p-2 md:p-3 bg-red-950/30">
            {initError}
          </p>
        )}

        {canUseLive && !liveReady && !initError && (
          <p className="text-xs text-slate-500 text-center py-3 md:py-4">
            {t.connecting}
          </p>
        )}

        {canUseLive && liveReady && messages.length === 0 && (
          <p className="text-xs md:text-sm text-slate-400 italic border border-dashed border-white/10 rounded-2xl p-3 md:p-5 text-center">
            {t.welcome}
          </p>
        )}
        {canUseLive &&
          liveReady &&
          messages.map((msg) => <LiveBubble key={msg.id} msg={msg} />)}
      </div>

      {canUseLive && (
        <div className="flex-none p-3 md:p-5 bg-transparent  border-t border-white/5">
          <form onSubmit={onSendMessage} className="space-y-2">
            <div className="flex gap-2 items-end">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    onSendMessage(e);
                  }
                }}
                placeholder={t.placeholder}
                disabled={showOffline || !liveReady || sending || !canUseLive}
                rows={2}
                className="flex-1 resize-none rounded-lg md:rounded-xl bg-black/40 border border-white/10 px-3 md:px-4 py-2 md:py-3 text-xs md:text-[13px] text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#00BCF0]/40 disabled:opacity-50"
              />
              <motion.button
                type="submit"
                disabled={
                  showOffline ||
                  !liveReady ||
                  sending ||
                  !input.trim() ||
                  !canUseLive
                }
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="shrink-0 h-[44px] md:h-[52px] px-3 md:px-4 rounded-lg md:rounded-xl bg-gradient-to-r from-[#007BFF] to-[#05DFAD] text-white disabled:opacity-40 flex items-center justify-center">
                <Send size={20} strokeWidth={2.5} />
              </motion.button>
            </div>
            {liveReady && (
              <p className="text-[8px] md:text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 text-center">
                {t.send} · Enter
              </p>
            )}
          </form>
        </div>
      )}
    </div>
  );
}
